//src/pages/Expedicao.js


import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import * as XLSX from "xlsx";
import "./Expedicao.css";
import { toast } from "react-toastify";
import {
  CheckSquare,
  ClipboardList,
  Download,
  FileSpreadsheet,
  MapPin,
  PackageCheck,
  Printer,
  Save,
  Search,
  Send,
  Truck,
} from "lucide-react";
import ComProtecao from "../components/ComProtecao";

const STATUS_COLUNAS = [
  { key: "preparacao", label: "Em preparação" },
  { key: "transporte", label: "Em transporte" },
  { key: "enviado", label: "Enviados" },
];

function ExpedicaoConteudo() {
  const [resgates, setResgates] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [filtros, setFiltros] = useState({
    cliente: "",
    cidade: "",
    estado: "",
    empresa: "",
    inicio: "",
    fim: "",
    premio: "",
  });

  useEffect(() => {
    carregarResgates();
  }, []);

  const carregarResgates = async () => {
    try {
      const { data } = await axios.get("/resgates-expedicao/agrupados");
      setResgates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar resgates:", err);
    }
  };

  const chaveItem = (item) => item.resgate_ids.join("-");

  const toggleSelecionado = (item) => {
    const chave = chaveItem(item);
    const existe = selecionados.find((sel) => chaveItem(sel) === chave);

    if (existe) {
      setSelecionados(selecionados.filter((sel) => chaveItem(sel) !== chave));
    } else {
      setSelecionados([...selecionados, item]);
    }
  };

  const gerarEtiquetas = async () => {
    const agrupadoPorCliente = {};

    for (const item of selecionados) {
      const itemAtual = resgates.find((r) => chaveItem(r) === chaveItem(item));
      const tipoAtual = itemAtual?.tipo_envio || "PAC";

      if (!agrupadoPorCliente[item.usuario_id]) {
        agrupadoPorCliente[item.usuario_id] = {
          nome: item.nome_usuario,
          email: item.email,
          destino: item.destino_envio,
          pedidos: [],
        };
      }

      agrupadoPorCliente[item.usuario_id].pedidos.push({
        nome_premio: item.nome_premio,
        quantidade: item.quantidade,
        pontos_total: item.pontos_total,
        ids: item.resgate_ids,
        tipo_envio: tipoAtual,
      });
    }

    try {
      const response = await axios.post(
        "/expedicao/gerar-etiqueta",
        { itensPorCliente: Object.values(agrupadoPorCliente) },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `etiquetas-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Erro ao gerar etiquetas:", err);
      toast.error("Erro ao gerar etiquetas PDF.");
    }
  };

  const avancarStatus = async (item) => {
    try {
      await axios.put("/resgates/expedicao/avancar", {
        resgate_ids: item.resgate_ids,
        status_atual: item.status,
      });

      toast.success("Status atualizado com sucesso!");
      carregarResgates();
    } catch (err) {
      console.error("Erro ao atualizar status:", err);
      toast.error("Erro ao atualizar status do pedido.");
    }
  };

  const handleObservacaoChange = (resgateIds, novaObs) => {
    setResgates((prev) =>
      prev.map((r) =>
        chaveItem(r) === resgateIds.join("-")
          ? { ...r, observacoes: novaObs }
          : r
      )
    );
  };

  const salvarObservacao = async (item) => {
    try {
      await axios.put(`/resgates/observacao/${item.resgate_ids[0]}`, {
        novaObservacao: item.observacoes,
      });

      toast.success("Observações salvas!");
    } catch (err) {
      console.error("Erro ao salvar observações:", err);
      toast.error("Erro ao salvar observações");
    }
  };

  const handleRastreioChange = (item, rastreio) => {
    setResgates((prev) =>
      prev.map((r) =>
        chaveItem(r) === chaveItem(item) ? { ...r, codigo_rastreio: rastreio } : r
      )
    );
  };

  const reimprimirEtiqueta = async (item) => {
    try {
      const response = await axios.post(
        "/expedicao/gerar-etiqueta",
        {
          itensPorCliente: [
            {
              nome: item.nome_usuario,
              email: item.email,
              destino: item.destino_envio,
              pedidos: [
                {
                  nome_premio: item.nome_premio,
                  quantidade: item.quantidade,
                  pontos_total: item.pontos_total,
                  ids: item.resgate_ids,
                  tipo_envio: item.tipo_envio || "PAC",
                },
              ],
            },
          ],
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `etiqueta-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Erro ao reimprimir etiqueta:", err);
      toast.error("Erro ao gerar etiqueta PDF.");
    }
  };

  const atualizarTipoEnvio = async (resgateId, tipo) => {
    try {
      await axios.put(`/resgates/tipo-envio/${resgateId}`, { tipo_envio: tipo });

      toast.success("Tipo de envio atualizado!");

      setResgates((prev) =>
        prev.map((r) =>
          r.resgate_ids.includes(resgateId) ? { ...r, tipo_envio: tipo } : r
        )
      );
    } catch (err) {
      toast.error("Erro ao atualizar tipo de envio.");
    }
  };

  const exportarExcel = () => {
    const planilha = resgates.map((item) => ({
      Cliente: item.nome_usuario,
      Empresa: item.empresa,
      Prêmio: item.nome_premio,
      Quantidade: item.quantidade,
      Voltagem: item.voltagem || "-",
      "Pontos Totais": item.pontos_total,
      Status: item.status,
      "Resgates IDs": item.resgate_ids.join(", "),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(planilha);

    XLSX.utils.book_append_sheet(wb, ws, "Expedição");
    XLSX.writeFile(wb, `expedicao-${Date.now()}.xlsx`);
  };

  const aplicarFiltros = (item) =>
    (item.nome_usuario || "")
      .toLowerCase()
      .includes((filtros.cliente || "").toLowerCase()) &&
    (item.cidade || "")
      .toLowerCase()
      .includes((filtros.cidade || "").toLowerCase()) &&
    (item.uf || "")
      .toLowerCase()
      .includes((filtros.estado || "").toLowerCase()) &&
    (item.empresa || "")
      .toLowerCase()
      .includes((filtros.empresa || "").toLowerCase()) &&
    (!filtros.inicio || new Date(item.data_resgate) >= new Date(filtros.inicio)) &&
    (!filtros.fim || new Date(item.data_resgate) <= new Date(filtros.fim)) &&
    (item.nome_premio || "")
      .toLowerCase()
      .includes((filtros.premio || "").toLowerCase());

  const resgatesFiltrados = useMemo(
    () => resgates.filter(aplicarFiltros),
    [resgates, filtros]
  );

  const resumo = useMemo(() => {
    return STATUS_COLUNAS.reduce((acc, status) => {
      acc[status.key] = resgatesFiltrados.filter((r) => r.status === status.key).length;
      return acc;
    }, {});
  }, [resgatesFiltrados]);

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });

  const renderCard = (item) => {
    const selecionado = selecionados.some((sel) => chaveItem(sel) === chaveItem(item));

    return (
      <div key={chaveItem(item)} className={`expedicaoAdmin-item ${item.status}`}>
        <div className="expedicaoAdmin-itemTop">
          <label className="expedicaoAdmin-check">
            <input
              type="checkbox"
              checked={selecionado}
              onChange={() => toggleSelecionado(item)}
            />
            <span />
          </label>

          <div className="expedicaoAdmin-itemTitle">
            <strong>{item.nome_premio}</strong>

            <div className="expedicaoAdmin-badges">
              <span>{item.quantidade} un.</span>
              <span>{item.tipo_envio || "PAC"}</span>
              {item.voltagem && <span>{item.voltagem}</span>}
            </div>
          </div>
        </div>

        <div className="expedicaoAdmin-infoGrid">
          <div>
            <small>Cliente</small>
            <strong>{item.nome_usuario}</strong>
          </div>

          <div>
            <small>Empresa</small>
            <strong>{item.empresa || "—"}</strong>
          </div>

          <div>
            <small>Local</small>
            <strong>
              {[item.cidade, item.uf].filter(Boolean).join(" / ") || "—"}
            </strong>
          </div>

          <div>
            <small>Pontos</small>
            <strong>{formatarNumero(item.pontos_total)} pts</strong>
          </div>
        </div>

        <details className="expedicaoAdmin-details">
          <summary>Ver detalhes e ações</summary>

          <div className="expedicaoAdmin-detalhesBox">
            <div className="expedicaoAdmin-destino">
              <MapPin size={14} />
              <span>{item.destino_envio || "Destino não informado"}</span>
            </div>

            <div className="expedicaoAdmin-pedidos">
              Pedidos incluídos: <strong>{item.resgate_ids.join(", ")}</strong>
            </div>

            <textarea
              className="expedicaoAdmin-textarea"
              placeholder="Adicionar observações..."
              value={item.observacoes || ""}
              onChange={(e) => handleObservacaoChange(item.resgate_ids, e.target.value)}
            />

            <div className="expedicaoAdmin-actionsGrid">
              <button
                type="button"
                className="expedicaoAdmin-btn secondary"
                onClick={() => salvarObservacao(item)}
              >
                <Save size={14} />
                Salvar observações
              </button>

              <select
                value={item.tipo_envio || "PAC"}
                onChange={(e) => atualizarTipoEnvio(item.resgate_ids[0], e.target.value)}
              >
                <option value="PAC">PAC - Envio Normal</option>
                <option value="SEDEX">SEDEX - Entrega Rápida</option>
              </select>
            </div>

            {item.status !== "enviado" ? (
              <button
                type="button"
                className="expedicaoAdmin-btn primary full"
                onClick={() => avancarStatus(item)}
              >
                <Truck size={15} />
                Avançar para próxima etapa
              </button>
            ) : (
              <div className="expedicaoAdmin-rastreio">
                <label>Código de rastreio</label>

                <input
                  type="text"
                  placeholder="Código de rastreio"
                  value={item.codigo_rastreio || ""}
                  onChange={(e) => handleRastreioChange(item, e.target.value)}
                />

                <button
                  type="button"
                  className="expedicaoAdmin-btn primary full"
                  onClick={() => reimprimirEtiqueta(item)}
                >
                  <Printer size={15} />
                  Reimprimir etiqueta
                </button>
              </div>
            )}
          </div>
        </details>
      </div>
    );
  };

  return (
    <div className="expedicaoAdmin-page">
      <div className="expedicaoAdmin-topbar">
        <div>
          <span className="expedicaoAdmin-kicker">Logística de prêmios</span>

          <h2>Expedição de Prêmios</h2>

          <p>
            Acompanhe o fluxo dos resgates por etapa, gere etiquetas e avance os
            pedidos com uma visão operacional mais compacta.
          </p>
        </div>

        <button
          type="button"
          className="expedicaoAdmin-btn primary"
          onClick={exportarExcel}
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      <div className="expedicaoAdmin-filtrosCard">
        <div className="expedicaoAdmin-filtros">
          <div className="expedicaoAdmin-searchInput">
            <Search size={15} />
            <input
              placeholder="Cliente"
              value={filtros.cliente}
              onChange={(e) => setFiltros({ ...filtros, cliente: e.target.value })}
            />
          </div>

          <input
            placeholder="Empresa"
            value={filtros.empresa}
            onChange={(e) => setFiltros({ ...filtros, empresa: e.target.value })}
          />

          <input
            placeholder="Cidade"
            value={filtros.cidade}
            onChange={(e) => setFiltros({ ...filtros, cidade: e.target.value })}
          />

          <input
            placeholder="UF"
            value={filtros.estado}
            onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
          />

          <input
            type="date"
            value={filtros.inicio}
            onChange={(e) => setFiltros({ ...filtros, inicio: e.target.value })}
          />

          <input
            type="date"
            value={filtros.fim}
            onChange={(e) => setFiltros({ ...filtros, fim: e.target.value })}
          />

          <input
            placeholder="Prêmio"
            value={filtros.premio}
            onChange={(e) => setFiltros({ ...filtros, premio: e.target.value })}
          />
        </div>

        {selecionados.length > 0 && (
          <div className="expedicaoAdmin-selecaoBar">
            <div>
              <CheckSquare size={16} />
              <strong>{selecionados.length}</strong> item(ns) selecionado(s)
            </div>

            <button
              type="button"
              className="expedicaoAdmin-btn success"
              onClick={gerarEtiquetas}
            >
              <Download size={15} />
              Gerar etiquetas
            </button>
          </div>
        )}
      </div>

      <div className="expedicaoAdmin-kanban">
        {STATUS_COLUNAS.map((coluna) => {
          const itens = resgatesFiltrados.filter((item) => item.status === coluna.key);

          return (
            <section key={coluna.key} className={`expedicaoAdmin-coluna ${coluna.key}`}>
              <div className="expedicaoAdmin-colunaHeader">
                <div>
                  <span>{coluna.label}</span>
                  <strong>{resumo[coluna.key] || 0}</strong>
                </div>

                {coluna.key === "preparacao" && <ClipboardList size={18} />}
                {coluna.key === "transporte" && <Truck size={18} />}
                {coluna.key === "enviado" && <PackageCheck size={18} />}
              </div>

              <div className="expedicaoAdmin-lista">
                {itens.length === 0 ? (
                  <div className="expedicaoAdmin-empty">Nenhum pedido nesta etapa.</div>
                ) : (
                  itens.map(renderCard)
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default function Expedicao() {
  return (
    <ComProtecao rota="resgates" tipo="pode_ler">
      <ExpedicaoConteudo />
    </ComProtecao>
  );
}